import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@client/app/modules/translate.module';
import { JiraConnectorRepo } from '@client/app/feature-modules/admin/modules/admin-jira-connector/services/jira-connector.repo';
import { NgbModalOpenerService } from '@client/app/services/ngb-modal-opener/ngb-modal-opener.service';
import { SessionService } from '@monsido/core/session/session.service';
import { CreateJiraIssueModalComponent } from '@client/app/components/jira/create-issue-modal.component';

interface JiraIssueError {
    jira_issue_url: string | null;
    error_page_id: number;
}

type JiraCheck = unknown;

type JiraPage = unknown;

@Component({
    selector: 'jira-issue-buttons',
    standalone: true,
    imports: [CommonModule, NgbTooltipModule, TranslateModule],
    template: `
        <ng-container *ngIf="hasJiraConnector">
            <button *ngIf="error?.jira_issue_url === null"
                    class="btn btn-border-radius btn-secondary mr-1"
                    triggers="focus hover"
                    ngbTooltip="{{ 'Create issue in Jira' | translate }}"
                    container="body"
                    (click)="createJiraIssue(error)"
                    [disabled]="isCreatingJiraIssue">
                <span *ngIf="!isCreatingJiraIssue" class="fab fa-jira" aria-hidden="true"></span>
                <span *ngIf="isCreatingJiraIssue" class="fas fa-spinner fa-spin" role="status" aria-hidden="true"></span>
                <span class="ml-1">{{ 'Create issue' | translate }}</span>
            </button>

            <button *ngIf="error?.jira_issue_url"
                    class="btn btn-border-radius btn-secondary mr-1"
                    triggers="focus hover"
                    ngbTooltip="{{ 'View issue in Jira' | translate }}"
                    container="body"
                    (click)="viewJiraIssue(error)">
                <span class="fab fa-jira" aria-hidden="true"></span>
                <span class="ml-1">{{ 'View issue' | translate }}</span>
            </button>
        </ng-container>
    `,
})
export class JiraIssueButtonsComponent {
    @Input() error: JiraIssueError | null = null;
    @Input() check: JiraCheck | null = null;
    @Input() page: JiraPage | null = null;
    @Input() hasJiraConnector: boolean = false;

    isCreatingJiraIssue: boolean = false;

    constructor (
        private jiraConnectorRepo: JiraConnectorRepo,
        private ngbModalOpenerService: NgbModalOpenerService,
        private sessionService: SessionService,
    ) {}

    createJiraIssue (error: JiraIssueError | null): void {
        if (!error) {
            return;
        }
        const modal = this.ngbModalOpenerService.open({
            component: CreateJiraIssueModalComponent,
            size: 'sm',
            resolve: {
                error: error,
                check: this.check,
                page: this.page,
                user: this.sessionService.me,
            },
        }) as { componentInstance: CreateJiraIssueModalComponent; close: () => void; dismiss: () => void };

        modal.componentInstance.close.subscribe((payload: { title: string; description: string }) => {
            this.isCreatingJiraIssue = true;
            this.jiraConnectorRepo.createIssue({
                issue_id: error.error_page_id,
                issue_type: 'accessibility',
                title: payload.title,
                description: payload.description,
            }).then(
                (response: { url: string }) => {
                    error.jira_issue_url = response.url;
                    this.isCreatingJiraIssue = false;
                    modal.close();
                },
                () => {
                    this.isCreatingJiraIssue = false;
                },
            );
        });

        modal.componentInstance.dismiss.subscribe(() => {
            modal.dismiss();
        });
    }

    viewJiraIssue (error: JiraIssueError | null): void {
        if (error?.jira_issue_url) {
            window.open(error.jira_issue_url, '_blank');
        }
    }
}
